"use client";

import { useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { CalendarPlus, CalendarDays, MapPin } from "lucide-react";
import {
  bySchedule,
  cn,
  formatTime,
  inScope,
  iso,
  ordinal,
  teamIndex,
  withAlpha,
  ALL_SCOPE,
  STATUS_META,
  STATUS_ORDER,
  type Itinerary,
  type Scope,
} from "@routecore/core";
import { useStudio } from "@routecore/app-shell";
import { ScopeFilter, PeriodNav, PrimaryButton } from "@routecore/controls";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function CalendarView() {
  const {
    employees,
    teams,
    itineraries,
    openItineraryModal,
    openDayModal,
    openEmployeeModal,
    openTeamModal,
    isPrivileged,
    canEditEmployee,
    canEditItinerary,
    toast,
  } = useStudio();
  const [cursor, setCursor] = useState(() => new Date());
  const [scope, setScope] = useState<Scope>(ALL_SCOPE);
  const teamOf = useMemo(() => teamIndex(employees), [employees]);
  const anyWritable = useMemo(
    () => isPrivileged || employees.some((e) => canEditEmployee(e)),
    [employees, isPrivileged, canEditEmployee],
  );

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const out: Date[] = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) out.push(d);
    return out;
  }, [cursor]);

  const byDate = useMemo(() => {
    const map = new Map<string, Itinerary[]>();
    for (const it of itineraries) {
      if (!inScope(it, scope, teamOf)) continue;
      const list = map.get(it.date) ?? [];
      list.push(it);
      map.set(it.date, list);
    }
    for (const list of map.values()) list.sort(bySchedule);
    return map;
  }, [itineraries, scope, teamOf]);

  const monthTotal = useMemo(() => {
    const s = iso(startOfMonth(cursor));
    const e = iso(endOfMonth(cursor));
    return itineraries.filter((i) => i.date >= s && i.date <= e && inScope(i, scope, teamOf))
      .length;
  }, [itineraries, cursor, scope, teamOf]);

  const todayStr = iso(new Date());

  return (
    <div className="mx-auto w-full max-w-[1600px] 2xl:max-w-none">
      {/* header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <CalendarDays className="size-3.5 text-accent" />
            <span className="text-[10px] font-bold tracking-[0.24em] text-ink-faint uppercase">
              {format(cursor, "yyyy")} · Month atlas
            </span>
          </div>
          <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            {format(cursor, "MMMM")}
            <span className="text-ink-faint">, plotted.</span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-line bg-porcelain px-3.5 py-2 text-xs font-bold text-ink-soft shadow-sm">
            {monthTotal} {monthTotal === 1 ? "itinerary" : "itineraries"}
          </span>
          <PeriodNav
            label={format(cursor, "MMM yyyy")}
            onPrev={() => setCursor((c) => addMonths(c, -1))}
            onNext={() => setCursor((c) => addMonths(c, 1))}
            onReset={() => setCursor(new Date())}
          />
          <PrimaryButton
            icon={CalendarPlus}
            disabled={!anyWritable}
            onClick={() => openItineraryModal({ presetDate: todayStr })}
          >
            {anyWritable ? "New itinerary" : "🔒 No bound profile"}
          </PrimaryButton>
        </div>
      </header>

      {/* team + employee filter */}
      <div className="mt-6">
        <ScopeFilter
          employees={employees}
          teams={teams}
          scope={scope}
          onChange={setScope}
          onAdd={isPrivileged ? () => openEmployeeModal() : undefined}
          onManageTeams={isPrivileged ? openTeamModal : undefined}
        />
      </div>

      {/* grid */}
      <div className="mt-4 overflow-x-auto pb-2">
        <div className="min-w-[960px] overflow-hidden rounded-2xl border border-line bg-line shadow-card">
          <div className="grid grid-cols-7 gap-px">
            {WEEKDAYS.map((w) => (
              <div
                key={w}
                className="bg-cream px-3 py-2.5 text-[10px] font-bold tracking-[0.18em] text-ink-soft uppercase"
              >
                {w}
              </div>
            ))}
            {days.map((day) => {
              const dateStr = iso(day);
              const items = byDate.get(dateStr) ?? [];
              const inMonth = isSameMonth(day, cursor);
              const isToday = dateStr === todayStr;
              const visible = items.slice(0, 3);
              const extra = items.length - visible.length;

              return (
                <div
                  key={dateStr}
                  onClick={() => {
                    if (!anyWritable) {
                      toast("No employee profile bound to your account.", "warn");
                      return;
                    }
                    openItineraryModal({ presetDate: dateStr });
                  }}
                  title={anyWritable ? "Click to add an itinerary" : "Read-only — no bound profile"}
                  className={cn(
                    "group relative flex min-h-[124px] flex-col gap-1 p-1.5 transition-colors 2xl:min-h-[136px] 2xl:p-2",
                    anyWritable ? "cursor-pointer" : "cursor-not-allowed",
                    inMonth ? "bg-porcelain" : "bg-out-of-month",
                    anyWritable && inMonth ? "hover:bg-black/[0.03]" : "",
                    anyWritable && !inMonth ? "hover:bg-black/[0.04]" : "",
                  )}
                >
                  <div className="flex items-center justify-between px-0.5">
                    <span
                      className={cn(
                        "grid size-6 place-items-center rounded-full text-xs font-bold transition-transform group-hover:scale-110",
                        isToday && "bg-accent text-porcelain shadow-sm",
                        !isToday && (inMonth ? "text-ink" : "text-ink-faint/70"),
                      )}
                    >
                      {format(day, "d")}
                    </span>
                    {items.length > 0 ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDayModal(dateStr);
                        }}
                        className="rounded-full px-1.5 py-0.5 text-[10px] font-bold text-ink-faint transition-colors hover:bg-ink hover:text-porcelain"
                      >
                        {items.length}
                      </button>
                    ) : (
                      <span className="grid size-5 place-items-center rounded-full text-ink-faint opacity-0 transition-opacity group-hover:opacity-60">
                        <CalendarPlus className="size-3" />
                      </span>
                    )}
                  </div>

                  <div className="mt-auto space-y-1">
                    {visible.map((it) => (
                      <Chip key={it.id} it={it} />
                    ))}
                    {extra > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDayModal(dateStr);
                        }}
                        className="w-full rounded-md px-1.5 py-0.5 text-left text-[10px] font-bold text-ink-soft transition-colors hover:bg-cream hover:text-ink"
                      >
                        +{extra} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* legend */}
      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
        {STATUS_ORDER.map((s) => (
          <span key={s} className="flex items-center gap-1.5 text-[11px] font-semibold text-ink-soft">
            <span className="size-2 rounded-full" style={{ backgroundColor: STATUS_META[s].color }} />
            {STATUS_META[s].label}
          </span>
        ))}
        <span className="ml-auto text-[11px] text-ink-faint">
          Click any day to add · click a chip to edit
        </span>
      </div>
    </div>
  );
}

function Chip({ it }: { it: Itinerary }) {
  const { openItineraryModal, canEditItinerary, toast } = useStudio();
  const c = it.employeeColor ?? "#8a8172";
  const faded = it.status === "completed" || it.status === "cancelled";
  const writable = canEditItinerary(it);

  return (
    <button
      type="button"
      title={`${it.employeeName ?? "Unassigned"} — ${ordinal(it.seq)} stop${
        it.appointmentTime ? ` at ${formatTime(it.appointmentTime)}` : ""
      }: ${it.title}${it.locationName ? ` @ ${it.locationName}` : ""} (${
        STATUS_META[it.status].label
      })${writable ? "" : " · 🔒 Read-only"}`}
      onClick={(e) => {
        e.stopPropagation();
        if (!writable) {
          toast("This itinerary is bound to another account.", "warn");
          return;
        }
        openItineraryModal({ itinerary: it });
      }}
      className={cn(
        "flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left transition-all",
        writable ? "hover:scale-[1.025] hover:shadow-sm active:scale-[0.99]" : "cursor-not-allowed",
        faded && "opacity-55",
      )}
      style={{ backgroundColor: withAlpha(c, 0.14) }}
    >
      <span className="shrink-0 text-[9px] font-black text-ink/45 tabular-nums">{it.seq}</span>
      <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: c }} />
      <span
        className={cn(
          "truncate text-[11px] leading-tight font-semibold text-ink/85",
          it.status === "cancelled" && "line-through decoration-ink/40",
        )}
      >
        {it.appointmentTime && (
          <span className="mr-1 font-black text-ink/55 tabular-nums">
            {it.appointmentTime}
          </span>
        )}
        {it.title}
      </span>
      {it.locationName && (
        <MapPin className="ml-auto size-2.5 shrink-0 text-ink/40" />
      )}
    </button>
  );
}
